/**
 * Load orchestration. Part of the composition root (CODE_RULES.md §3) — one of
 * only two modules permitted to reach across every feature layer.
 *
 * Owns the "what is currently loaded" state so no feature module needs
 * module-level mutable state (§4).
 */
import * as OBC from "@thatopen/components";
import { logger } from "./core/logger";
import { detectUploadKind, readValidatedFile } from "./security/file-validation";
import { loadIfc, setupIfcLoader } from "./viewer/ifc-loader";
import { createVisibilityController, type VisibilityController } from "./viewer/visibility";
import { createViewController, type ViewController } from "./viewer/views-2d";
import { openPdf, type PdfHandle } from "./pdf/pdf-document";
import type { Viewer } from "./viewer/world";
import type { AppShell, ViewMode } from "./ui/app-shell";
import type { UploadPanel } from "./ui/panels/upload-panel";
import type { PdfPanel } from "./ui/panels/pdf-panel";
import type { ToggleList } from "./ui/panels/toggle-list";

export interface SessionPanels {
  readonly shell: AppShell;
  readonly upload: UploadPanel;
  readonly pdf: PdfPanel;
  readonly storeys: ToggleList;
  readonly categories: ToggleList;
}

export type VisibilityKind = "storey" | "category";

export interface Session {
  handleFiles(files: readonly File[]): Promise<void>;
  setMode(mode: ViewMode): void;
  openPlan(id: string): void;
  /** No-op until a model is loaded, so the UI never needs to know the order. */
  setVisibility(kind: VisibilityKind, id: string, visible: boolean): Promise<void>;
  resetVisibility(): void;
}

export function createSession(viewer: Viewer, panels: SessionPanels): Session {
  let visibility: VisibilityController | null = null;
  let views: ViewController | null = null;
  let pdfHandle: PdfHandle | null = null;
  let mode: ViewMode = "3d";

  async function loadIfcFile(file: File): Promise<void> {
    const validated = await readValidatedFile(file, "ifc");
    if (!validated.ok) {
      panels.upload.showError(validated.error.message);
      return;
    }

    panels.upload.showProgress("Converting model", 0);
    const loader = viewer.components.get(OBC.IfcLoader);
    await setupIfcLoader(loader);

    const loaded = await loadIfc(loader, validated.value, file.name, (fraction) => {
      panels.upload.showProgress("Converting model", fraction);
    });
    if (!loaded.ok) {
      panels.upload.showError(loaded.error.message);
      return;
    }

    visibility = await createVisibilityController(viewer.components);
    views = await createViewController(viewer.components, viewer.world);

    panels.storeys.render(toItems(visibility.storeys));
    panels.categories.render(toItems(visibility.categories));
    panels.shell.setPlans(views.plans);
    panels.shell.setReady(true);
    panels.upload.hide();
  }

  async function loadPdfFile(file: File): Promise<void> {
    const validated = await readValidatedFile(file, "pdf");
    if (!validated.ok) {
      panels.upload.showError(validated.error.message);
      return;
    }
    const opened = await openPdf(validated.value);
    if (!opened.ok) {
      panels.upload.showError(opened.error.message);
      return;
    }
    if (pdfHandle !== null) await pdfHandle.dispose();
    pdfHandle = opened.value;
    panels.pdf.attach(pdfHandle);
    panels.shell.setReady(true);
  }

  return {
    async handleFiles(files) {
      for (const file of files) {
        // Detect by header, not filename: mobile pickers routinely rewrite or
        // strip extensions, and iOS cannot filter for .ifc at all.
        const kind = await detectUploadKind(file);
        if (kind === "ifc") {
          await loadIfcFile(file);
        } else if (kind === "pdf") {
          await loadPdfFile(file);
        } else {
          panels.upload.showError(`${file.name} is neither an IFC nor a PDF.`);
        }
      }
    },

    setMode(next) {
      mode = next;
      panels.shell.setMode(next);
      panels.pdf.setVisible(next === "pdf");
      if (next !== "plan") views?.close();
      logger.info("View mode changed", { mode: next });
    },

    openPlan(id) {
      if (mode !== "plan") return;
      views?.open(id);
    },

    async setVisibility(kind, id, visible) {
      if (visibility === null) return;
      await (kind === "storey"
        ? visibility.setStoreyVisible(id, visible)
        : visibility.setCategoryVisible(id, visible));
    },

    resetVisibility() {
      if (visibility === null) return;
      void visibility.showAll().then(() => {
        if (visibility === null) return;
        panels.storeys.render(toItems(visibility.storeys));
        panels.categories.render(toItems(visibility.categories));
      });
    },
  };
}

function toItems(
  groups: readonly { id: string; label: string; visible: boolean }[],
): { id: string; label: string; on: boolean }[] {
  return groups.map((group) => ({ id: group.id, label: group.label, on: group.visible }));
}
